import React, { useEffect, useRef, useState } from "react";
import { errorNotification } from "../Generic/Notifications";
import { Table, Tbody, Tcol, Thead, Trow } from "../Generic/Table";
import EmptyState from "../Generic/EmptyState";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as TaskActions from "./actions/taskActions";
import TaskService from "./services";
import { TASK_STATUSES } from "./TaskConstants";
import { beautifyKey } from "../util/util";
import Loader from "../Generic/Loader";

const Page = styled.div`
	display: flex;
	height: max-content;
	overflow-y: auto;
	overflow-x: hidden;
	flex-direction: column;
	text-align: left;
	width: 100%;
	padding: 1rem;
`;

const Header = styled.div`
	display: flex;
	font-size: 2rem;
	margin: 1rem 0 0 0.5rem;
`;

const CenteredContainer = styled.div`
	width: 100%;
	height: max-content;
	display: flex;
	flex-direction: column;
	align-items: center;
	margin: ${(props) => props.margin};
`;

function dateToString(date) {
	return new Date(date).toLocaleString();
}

const DATA_MODES = {
	APPEND: "APPEND",
	REPLACE: "REPLACE",
};

function scrollToRequiredItem(id) {
	const element = document.getElementById(id);
	if (!element) return;
	element.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
}

const TaskList = (props) => {
	const { machineID, kitchenId, taskList, setTaskList, lastClickedElement, clearState, setLastClickedElement } = props;
	const navigate = useNavigate();
	const { state } = useLocation();
	const preserveState = state?.preserveState && taskList?.length ? true : false;
	const [mode, setMode] = useState(preserveState ? DATA_MODES.APPEND : DATA_MODES.REPLACE);
	const [loading, setLoading] = useState(!preserveState);
	const [page, setPage] = useState(1);
	const [lastElement, setLastElement] = useState();
	const hasMore = useRef(true);
	const skipInitialCall = useRef(preserveState);

	async function getTaskList(page) {
		setLoading(true);
		const response = await TaskService.getRecentTasks(kitchenId, { page });
		const { data, error } = response;
		if (data) {
			const { tasks, count } = data;

			if (mode === DATA_MODES.APPEND) {
				const totalTasks = [...taskList, ...tasks];
				if (totalTasks.length >= count) {
					hasMore.current = false;
				}
				setTaskList(totalTasks);
			} else {
				if (tasks.length >= count) {
					hasMore.current = false;
				}
				setTaskList(tasks);
			}
		}
		if (error) {
			errorNotification(error);
			console.log(error);
		}
		setLoading(false);
	}

	//Creating a new observer which increments the page number once the observed element is fully visible
	const observer = useRef(
		new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					if (hasMore.current) {
						setMode(DATA_MODES.APPEND);
						setPage((page) => page + 1);
					}
				}
			},
			{
				//Threshold set to 1 for the page to increment only when the element is 100% visible
				//Threshold = % of element to be visible / 100
				threshold: 1,
			}
		)
	);

	useEffect(() => {
		//Update the element on which the call is made and set the observer on it once the list re-renders
		const currentElement = lastElement;
		const currentObserver = observer.current;

		if (currentElement) {
			currentObserver.observe(currentElement);
		}

		//Remove the observer once user exits the page
		return () => {
			if (currentElement) {
				currentObserver.unobserve(currentElement);
			}
		};
	}, [lastElement]);

	useEffect(() => {
		if (preserveState && lastClickedElement) {
			scrollToRequiredItem(lastClickedElement.id);
		} else {
			clearState();
		}
	}, [preserveState]);

	useEffect(() => {
		if (skipInitialCall.current) {
			skipInitialCall.current = false;
			return;
		}
		//Fires everytime page changes
		getTaskList(page);
	}, [page]);

	function navigateToTaskPage(taskId) {
		navigate(`/${machineID}/task/${taskId}`);
	}

	function renderTaskList(tasks, loading) {
		if (tasks.length) {
			let tasksRenderData = tasks.map((task, index, array) => {
				const { _id, taskType, taskGroupId, status, actualEndTime, actualStartTime } = task;
				const endTimeString = actualEndTime ? dateToString(actualEndTime) : "-";
				const startTimeString = actualStartTime ? dateToString(actualStartTime) : "-";
				return (
					<Trow
						id={_id}
						onClick={() => {
							setLastClickedElement({ id: _id });
							navigateToTaskPage(_id)
						}}
						innerRef={(node) => (index === array.length - 1 ? setLastElement(node) : null)}>
						<Tcol borderRadius="10px 0 0 10px">{_id}</Tcol>
						<Tcol>{beautifyKey(taskType)}</Tcol>
						<Tcol>{taskGroupId}</Tcol>
						<Tcol>{TASK_STATUSES[status]}</Tcol>
						<Tcol>{startTimeString}</Tcol>
						<Tcol borderRadius="0 10px 10px 0">{endTimeString}</Tcol>
					</Trow>
				);
			});
			return (
				<Table>
					<Trow head>
						<Thead>Task ID</Thead>
						<Thead>Task Type</Thead>
						<Thead>Task Group ID</Thead>
						<Thead>Task Status</Thead>
						<Thead>Start Time</Thead>
						<Thead>End Time</Thead>
					</Trow>
					<Tbody>{tasksRenderData}</Tbody>
				</Table>
			);
		} else if (!loading) {
			return <EmptyState retry={getTaskList} />;
		}
	}

	return (
		<Page>
			<Header>Recent Tasks</Header>
			{renderTaskList(taskList, loading)}
			{loading ? (
				<CenteredContainer>
					<Loader />
				</CenteredContainer>
			) : (
				""
			)}
		</Page>
	);
};

const mapStateToProps = (state) => {
	const { task, landing } = state;
	return { ...task, ...landing };
};

const mapDispatchToProps = (dispatch) => ({
	...bindActionCreators(
		{
			...TaskActions,
		},
		dispatch
	),
});

export default connect(mapStateToProps, mapDispatchToProps)(TaskList);
